import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';

/** Distinguishes "never loaded" from any real key value (including null/undefined). */
const UNLOADED = Symbol('unloaded');

/**
 * Defers a below-the-fold load until its host scrolls near the viewport. Emits `(deferLoad)` the
 * first time the host intersects, and again whenever `[appDeferLoad]` (a params key, e.g. a
 * filter+granularity version) changes while the host is on screen. Off-screen key changes are
 * picked up the next time the host becomes visible — so a parent can fire its visible analytics
 * requests first and let the rest load on scroll without hammering the backend on init.
 *
 * The host must reserve height (e.g. a fixed-height card or a full-width sentinel) so stacked
 * deferred sections don't all collapse to the bottom and trigger at once.
 */
@Directive({
  selector: '[appDeferLoad]',
  standalone: false,
})
export class DeferLoadDirective implements OnInit, OnChanges, OnDestroy {
  @Input('appDeferLoad') key: unknown;
  @Output() deferLoad = new EventEmitter<void>();

  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly zone = inject(NgZone);

  private observer: IntersectionObserver | null = null;
  private visible = false;
  private loadedKey: unknown = UNLOADED;

  ngOnInit(): void {
    // No IntersectionObserver (very old browsers / SSR) → load immediately so nothing stays hidden.
    if (typeof IntersectionObserver === 'undefined') {
      this.visible = true;
      this.maybeEmit();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) {
          this.visible = false;
          return;
        }
        this.visible = true;
        // IntersectionObserver may invoke outside Angular's zone; re-enter so the emitted
        // handler and its change detection run normally.
        this.zone.run(() => this.maybeEmit());
      },
      { rootMargin: '200px' },
    );
    this.observer.observe(this.host.nativeElement);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only react to later key changes; the host isn't observed yet on first change.
    if (changes['key'] && !changes['key'].firstChange) {
      this.maybeEmit();
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private maybeEmit(): void {
    if (!this.visible || this.key == null || this.key === this.loadedKey) {
      return;
    }
    this.loadedKey = this.key;
    this.deferLoad.emit();
  }
}
